import { createContext, type ReactNode, useContext, useId } from 'react';
import { cn } from '../cn.ts';

interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Accessibility props for a control inside a <FormField>: id, aria-describedby, aria-invalid,
 * required. Props passed explicitly to the control win.
 */
export function useFieldControl(props: {
  id?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling' | undefined;
  required?: boolean | undefined;
}) {
  const field = useContext(FieldContext);
  return {
    id: props.id ?? field?.id,
    'aria-describedby': props['aria-describedby'] ?? field?.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field?.invalid ? true : undefined),
    required: props.required ?? (field?.required || undefined),
    invalid: props['aria-invalid'] === true || props['aria-invalid'] === 'true' || Boolean(field?.invalid),
  };
}

export interface FormFieldProps {
  label: ReactNode;
  /** Help text under the control. */
  hint?: ReactNode;
  /** Error message; marks the control invalid and replaces the hint. */
  error?: ReactNode;
  required?: boolean;
  /** Use a fixed id for the control (otherwise generated). */
  id?: string;
  className?: string;
  children: ReactNode;
}

/** Label + control + hint/error, wired for screen readers. Wrap one Input/Textarea/Select/Checkbox. */
export function FormField({ label, hint, error, required = false, id, className, children }: FormFieldProps) {
  const generated = useId();
  const controlId = id ?? `field-${generated}`;
  const messageId = `${controlId}-message`;
  const message = error ?? hint;
  return (
    <FieldContext.Provider
      value={{
        id: controlId,
        describedBy: message ? messageId : undefined,
        invalid: Boolean(error),
        required,
      }}
    >
      <div className={cn('flex flex-col min-w-0 gap-1', className)}>
        <label htmlFor={controlId} className="mb-0.5 text-heading-3 text-n-slate-12">
          {label}
          {required && (
            <span aria-hidden="true" className="ms-0.5 text-n-ruby-9">
              *
            </span>
          )}
        </label>
        {children}
        {message && (
          <p
            id={messageId}
            className={cn('mt-1 mb-0 text-label-small', error ? 'text-n-ruby-9' : 'text-n-slate-11')}
            role={error ? 'alert' : undefined}
          >
            {message}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}
